import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import "./EditAdPage.css";
import { API_BASE_URL } from "../config";

export default function EditAdPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // исходные данные
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // справочники
  const [categories, setCategories] = useState([]);
  const [phones, setPhones] = useState([]);

  // форма
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  // фото
  const [existingImages, setExistingImages] = useState([]); // [{id, url, marked:false}]
  const [newFiles, setNewFiles] = useState([]);            // [File]
  const [newPreviews, setNewPreviews] = useState([]);      // [dataURL]

  // флаги
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // загрузка объявления
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/ads/${id}/`);
        if (cancel) return;

        setAd(data);
        setTitle(data.title || "");
        setDescription(data.description || "");
        setPrice(String(data.price ?? ""));
        setLocation(data.location || "");
        setCategory(String(data.category ?? ""));

        // фото
        const imgs = Array.isArray(data.images) ? data.images : [];
        setExistingImages(
          imgs.map((im) => ({
            id: im.id,
            url: im.image?.startsWith("http")
              ? im.image
              : `${API_BASE_URL}${im.image}`,
            marked: false,
          }))
        );

        // телефон (если был)
        if (data.contact_phone) setContactPhone(data.contact_phone);
      } catch (e) {
        setErr("Δεν βρέθηκε η αγγελία ή δεν έχετε πρόσβαση.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  // категории
  useEffect(() => {
    axios.get("/api/categories/").then((res) => {
      setCategories(res.data || []);
    }).catch(() => {});
  }, []);

  // подтверждённые телефоны
  useEffect(() => {
    axios.get("/api/phone-numbers/")
      .then((res) => setPhones(res.data || []))
      .catch(() => {});
  }, []);

  const markedToDelete = useMemo(
    () => existingImages.filter((im) => im.marked).map((im) => im.id),
    [existingImages]
  );

  const onPickNewFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // ограничим условно до 10
    const selected = files.slice(0, 10);
    setNewFiles((prev) => [...prev, ...selected]);

    // превью
    Promise.all(
      selected.map(
        (file) =>
          new Promise((resolve) => {
            const fr = new FileReader();
            fr.onloadend = () => resolve(fr.result);
            fr.readAsDataURL(file);
          })
      )
    ).then((urls) => {
      setNewPreviews((prev) => [...prev, ...urls]);
    });
  };

  const toggleMark = (imgId) => {
    setExistingImages((prev) =>
      prev.map((im) =>
        im.id === imgId ? { ...im, marked: !im.marked } : im
      )
    );
  };

  const removeNewPreview = (idx) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));
    setNewPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    if (!title.trim()) return "Ο τίτλος είναι υποχρεωτικός.";
    if (!description.trim()) return "Η περιγραφή είναι υποχρεωτική.";
    if (!price || isNaN(price) || Number(price) <= 0) return "Εισάγετε έγκυρη τιμή.";
    if (!category) return "Επιλέξτε κατηγορία.";
    if (!location.trim()) return "Η τοποθεσία είναι υποχρεωτική.";
    if (!contactPhone) return "Επιλέξτε επιβεβαιωμένο τηλέφωνο.";
    return "";
  };

  const saveChanges = async () => {
    setSaveMsg("");
    const v = validate();
    if (v) {
      setSaveMsg(v);
      return;
    }
    if (!localStorage.getItem("token")) {
      setSaveMsg("");
      window.dispatchEvent(
        new CustomEvent("open-login-modal", {
          detail: { message: "Παρακαλούμε συνδεθείτε για να επεξεργαστείτε αυτήν τη αγγελία." },
        })
      );
      return;
    }

    try {
      setSaving(true);

      // 1) PATCH — поля + новые фото
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      fd.append("price", price);
      fd.append("location", location);
      fd.append("category", category);
      fd.append("contact_phone", contactPhone);

      newFiles.forEach((file) => fd.append("images", file));

      await axios.patch(`/api/ads/${id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // 2) Удаляем отмеченные изображения (если есть)
      if (markedToDelete.length) {
        await Promise.all(
          markedToDelete.map((imgId) =>
            axios.delete(`/api/ads/${id}/images/${imgId}/`)
          )
        );
      }

      // 3) Перезагрузим объявление и очистим черновые состояния
      const { data } = await axios.get(`/api/ads/${id}/`);
      setAd(data);

      setExistingImages(
        (Array.isArray(data.images) ? data.images : []).map((im) => ({
          id: im.id,
          url: im.image?.startsWith("http")
            ? im.image
            : `${API_BASE_URL}${im.image}`,
          marked: false,
        }))
      );
      setNewFiles([]);
      setNewPreviews([]);

      setSaveMsg("✅ Οι αλλαγές αποθηκεύτηκαν.");
      // можно вернуть назад:
      // setTimeout(() => navigate(-1), 800);
    } catch (e) {
      console.error(e);
      setSaveMsg("❌ Κάτι πήγε στραβά. Προσπαθήστε ξανά.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="editad-loading">Φόρτωση...</div>;
  if (err) return <div className="editad-error">{err}</div>;
  if (!ad) return null;

  return (
    <div className="editad-page">
      <button className="editad-back" onClick={() => navigate(-1)}>← Πίσω</button>
      <h2 className="editad-title">Επεξεργασία αγγελίας</h2>

      <form className="editad-form" onSubmit={(e) => { e.preventDefault(); saveChanges(); }}>
        <label className="editad-label">
          Τίτλος
          <input
            className="editad-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Τίτλος"
          />
        </label>

        <label className="editad-label">
          Περιγραφή
          <textarea
            className="editad-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Περιγραφή"
          />
        </label>

        <label className="editad-label">
          Τιμή
          <div className="editad-price">
            <span className="editad-euro">€</span>
            <input
              className="editad-input"
              value={price}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                setPrice(digits.slice(0, 8));
              }}
              placeholder="Τιμή σε ευρώ"
            />
          </div>
        </label>

        <label className="editad-label">
          Τοποθεσία
          <input
            className="editad-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Πόλη / Περιοχή"
          />
        </label>

        <label className="editad-label">
          Κατηγορία
          <select
            className="editad-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">— Επιλέξτε —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>

        <label className="editad-label">
          Τηλέφωνο επικοινωνίας (μόνο επιβεβαιωμένα)
          <select
            className="editad-select"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          >
            <option value="">— Επιλέξτε αριθμό —</option>
            {phones.map((p) => (
              <option key={p.id} value={p.phone}>
                {p.phone} {p.is_verified ? "✅" : "❌"}
              </option>
            ))}
          </select>
        </label>

        <div className="editad-block">
          <h3 className="editad-subtitle">Υπάρχουσες φωτογραφίες</h3>
          {existingImages.length === 0 ? (
            <div className="editad-muted">Δεν υπάρχουν εικόνες.</div>
          ) : (
            <div className="editad-photos-grid">
              {existingImages.map((im) => (
                <div
                  key={im.id}
                  className={`editad-photo ${im.marked ? "is-marked" : ""}`}
                >
                  <img src={im.url} alt="" />
                  <button
                    type="button"
                    className="editad-photo-mark"
                    onClick={() => toggleMark(im.id)}
                    title={im.marked ? "Αναίρεση διαγραφής" : "Σήμανση για διαγραφή"}
                  >
                    {im.marked ? "↺" : "✕"}
                  </button>
                </div>
              ))}
            </div>
          )}
          {markedToDelete.length > 0 && (
            <div className="editad-note">
              Θα διαγραφούν: {markedToDelete.length} φωτογραφία/ες (μετά την αποθήκευση).
            </div>
          )}
        </div>

        <div className="editad-block">
          <h3 className="editad-subtitle">Προσθήκη νέων φωτογραφιών</h3>
          <input type="file" accept="image/*" multiple onChange={onPickNewFiles} />
          {newPreviews.length > 0 && (
            <div className="editad-photos-grid">
              {newPreviews.map((src, i) => (
                <div key={i} className="editad-photo">
                  <img src={src} alt={`new-${i}`} />
                  <button
                    type="button"
                    className="editad-photo-mark"
                    onClick={() => removeNewPreview(i)}
                    title="Αφαίρεση"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {saveMsg && <div className="editad-message">{saveMsg}</div>}

        <div className="editad-actions">
          <button
            type="button"
            className="editad-btn cancel"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Ακύρωση
          </button>
          <button
            type="submit"
            className="editad-btn primary"
            disabled={saving}
          >
            {saving ? "Αποθήκευση..." : "Αποθήκευση αλλαγών"}
          </button>
        </div>
      </form>
    </div>
  );
}
